// The shared asset library in the online IDE (docs/SHARED_ASSETS_DESIGN.md):
// - the account dialog's sections: the remembered answer to "Share it?",
//   the member's shared assets, and the programs signed in to the library;
// - library tokens for the World Editor, which finds and fetches shared
//   assets with one, never with the member's session;
// - the desktop World Editor's sign-in. The editor opens the IDE at
//   ?link=library&challenge=…&state=…&name=…, where challenge is the SHA-256
//   of a secret only the editor knows. Once the member allows it, the IDE
//   gives the server a one-time code for that state. The editor, which asks
//   the server with its state and secret every few seconds, then gets its
//   token. It needs no address of its own for the browser to come back to,
//   so no firewall asks about it, and the browser can be on another device.

const TOKEN_KEY = "b3d-ide:library-token";
const STATUS = { making: "being made", checking: "being checked", held: "waiting for a moderator", public: "in the library",
  refused: "refused", hidden: "hidden by a moderator", failed: "not made" };

const plural = (count, one, many = `${one}s`) => `${count.toLocaleString("en")} ${count === 1 ? one : many}`;
const when = (time) => new Date(time).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

// ide: { cloud, $, status, notice, ask, closeMenu }.
export function setupLibrary(ide) {
  const { cloud, $ } = ide;
  const available = () => !!(cloud && cloud.config && cloud.config.library);
  const signedIn = () => !!(cloud && cloud.user);

  // ------------------------------------------------------------- tokens

  // The World Editor's token: kept in this browser for the account it was
  // made for, and made again once the server no longer has it (the member
  // signed it out, or it went unused for 90 days). Null when signed out.
  async function token() {
    if (!available() || !signedIn()) return null;
    let kept = null;
    try {
      kept = JSON.parse(localStorage.getItem(TOKEN_KEY));
    } catch {}
    if (kept && kept.user === cloud.user.id) {
      try {
        const { tokens } = await cloud.request("GET", "library/tokens");
        if (tokens.some((each) => each.id === kept.id)) return kept.token;
      } catch (error) {
        if (error.status !== 401) return kept.token;
        return null;
      }
    }
    const made = await cloud.request("POST", "library/tokens", { json: { name: "World Editor in the online IDE" } });
    try {
      localStorage.setItem(TOKEN_KEY, JSON.stringify({ user: cloud.user.id, id: made.id, token: made.token }));
    } catch {}
    return made.token;
  }

  // What the World Editor is told when it starts: where the library is, and
  // its token, or none (it then asks the member to sign in to the IDE).
  async function editorAccess() {
    if (!available()) return { server: "", token: "" };
    let made = null;
    try {
      made = await token();
    } catch (error) {
      console.error("The library's token could not be made:", error);
    }
    return { server: location.origin, token: made || "" };
  }

  // ------------------------------------------------- the desktop sign-in

  // ?link=library&challenge=…&state=…&name=…, once at start.
  async function linkFromUrl(params) {
    if (params.get("link") !== "library") return;
    const challenge = params.get("challenge") || "";
    const signIn = params.get("state") || "";
    const name = (params.get("name") || "The World Editor").replace(/[\u0000-\u001f]/g, "").slice(0, 80);
    const rest = new URLSearchParams(location.search);
    for (const key of ["link", "challenge", "state", "name"]) rest.delete(key);
    history.replaceState(null, "", location.pathname + (rest.size ? "?" + rest : "") + location.hash);
    if (!/^[0-9a-f]{64}$/i.test(challenge) || !/^[A-Za-z0-9_-]{16,64}$/.test(signIn)) {
      ide.notice("This sign-in link is not complete. Try signing in from the World Editor again.", [], "warning");
      return;
    }
    if (!available()) {
      ide.notice("This site has no shared asset library, so the World Editor can't sign in here.", [], "warning");
      return;
    }
    // Signing in first, when needed.
    while (!signedIn()) {
      const yes = await ide.ask({ title: "Sign in to the shared asset library",
        message: `${name} wants to find and fetch shared assets with your Blitz3D+ account. Sign in first.`,
        action: "Sign in" });
      if (!yes) return;
      try {
        await cloud.signIn();
        ide.accountChanged();
      } catch (error) {
        if (error && error.name === "NotAllowedError") continue;
        ide.notice(`Signing in failed: ${error.message || error}`, [], "warning");
        return;
      }
    }
    const allow = await ide.ask({ title: "Allow the World Editor?",
      message: `Let ${name} find and fetch shared assets with your account, ${cloud.user.name}?`,
      detail: "It can search the library and fetch its models and pictures, within your limits, and nothing else. " +
        "Your account's Programs using the library list shows it, with Sign out.",
      action: "Allow" });
    if (!allow) {
      ide.status("The World Editor was not signed in.");
      return;
    }
    try {
      await cloud.request("POST", "library/codes", { json: { challenge: challenge.toLowerCase(), name, state: signIn } });
      ide.notice(`${name} is signed in to the shared asset library. You can go back to it now.`, [], "info");
    } catch (error) {
      ide.notice(`The World Editor could not be signed in: ${error.message || error}`, [], "warning");
    }
  }

  // ------------------------------------------------- the account dialog

  const element = (tag, text, className) => {
    const made = document.createElement(tag);
    if (text !== undefined) made.textContent = text;
    if (className) made.className = className;
    return made;
  };

  // Fills the dialog's library sections, when the site has the library and
  // the member is signed in.
  async function showAccount() {
    const section = $("library-account");
    section.hidden = !available() || !signedIn();
    if (section.hidden) return;
    const assets = $("library-assets"), tokens = $("library-tokens");
    assets.replaceChildren(element("li", "Loading…", "muted"));
    tokens.replaceChildren();
    try {
      const [me, listed] = await Promise.all([cloud.request("GET", "library/me"), cloud.request("GET", "library/tokens")]);
      $("library-sharing").value = me.sharing;
      assets.replaceChildren(...(me.assets.length ? me.assets.map((asset) => {
        const item = element("li");
        item.append(element("strong", asset.name), ` (${asset.kind === "picture" ? "a picture" : "a model"}): ${STATUS[asset.status] ||
          asset.status}`);
        if (asset.status === "public") item.append(`, used by ${plural(asset.uses, "member")}`);
        if (asset.rebateShare > 0) {
          item.append(`; ${Math.max(1, Math.round(asset.rebateShare * 100))}% of a day's energy came back`);
        }
        if (asset.reason && asset.status !== "public") item.append(` (${asset.reason})`);
        return item;
      }) : [element("li", "Nothing yet. When Blip makes a model or picture, you can share it.", "muted")]));
      tokens.replaceChildren(...(listed.tokens.length ? listed.tokens.map((each) => {
        const item = element("li");
        const signOut = element("button", "Sign out");
        signOut.type = "button";
        signOut.addEventListener("click", async () => {
          signOut.disabled = true;
          try {
            await cloud.request("DELETE", `library/tokens/${each.id}`);
            await showAccount();
          } catch (error) {
            signOut.disabled = false;
            $("account-error").textContent = error.message || String(error);
          }
        });
        item.append(element("span", `${each.name}, last used ${when(each.used)} `), signOut);
        return item;
      }) : [element("li", "None.", "muted")]));
    } catch (error) {
      assets.replaceChildren(element("li", `The library could not be reached: ${error.message || error}`, "error"));
    }
  }

  if (cloud) {
    $("library-sharing").addEventListener("change", async () => {
      const select = $("library-sharing");
      select.disabled = true;
      try {
        await cloud.request("PUT", "library/me/sharing", { json: { sharing: select.value } });
        ide.status({ ask: "Blip asks before he shares what he makes.", share: "Blip shares what he makes in the library.",
          keep: "Blip keeps what he makes just for you." }[select.value]);
      } catch (error) {
        $("account-error").textContent = error.message || String(error);
      } finally {
        select.disabled = false;
      }
    });
  }

  return { available, token, editorAccess, linkFromUrl, showAccount };
}
